import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FilterAttributeOptionComponentDef } from '@df/catalog-ui/listing/filter/filter-attribute-option.component.def';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Model } from '@df/catalog/model';
import { Client } from '@df/core/service/client';
import { FacetUrlModule } from '@df/module-facet-url/facet-url.module';
import { NgInject } from '@df/ng/ng-inject';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ICmsConfigResource } from '@icms/core/resource/config/icms-config.resource';
import { NAttributeOrder } from 'projects/app-default/src/app/cms/config/attribute-order.cms-config';
import { ExpandableComponent } from 'projects/app-default/src/app/ui/display/expandable/expandable.component';
import { SwatchComponent } from '../../../attribute/swatch/swatch.component';
import { NListing } from '../product-listing.interface';
@Component({
    selector: 'filter-attribute-option',
    templateUrl: './filter-attribute-option.html',
    standalone: true,
    imports: [ExpandableComponent, NgClass, GridModule, HrefComponent, FacetUrlModule, SwatchComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterAttributeOptionComponent<T extends Model> extends FilterAttributeOptionComponentDef<T> implements NListing.IFilterAttr {
    @NgInject(Client)
    client: Client;

    private iCmsConfigResource = inject(ICmsConfigResource);

    clear(event: Event, option: Catalog.IAttributeOption) {
        event.preventDefault();
        this.filter.unselect(option);
    }
    get zippyId() {
        return ['filter', this.filter.id];
    }
    getSelectedOptionsForFilter(lengthLimit = 20): string | undefined {
        if (this.filter) {
            const options = this.filter
                .getSelectedOptions()
                .map(i => i.label)
                .join(', ');
            this.detectChanges();
            return options.length > lengthLimit && options.indexOf(',', lengthLimit) !== -1
                ? options.slice(0, options.indexOf(',', lengthLimit)) + '...'
                : options;
        }
    }

    get isDefaultOpen(): boolean {
        return this.filter.isDefaultOpen;
    }

    get label(): string | undefined {
        return this.filter.attribute.data?.label;
    }

    get id(): string {
        return this.filter.config.id;
    }

    get isSwatch(): boolean {
        return !!this.filter.attribute.data?.isSwatch;
    }

    optionsWithMatches() {
        return this.filter.optionsWithMatches;
    }

    get hasAnyCurrentMatches(): boolean {
        return this.filter.hasAnyCurrentMatches;
    }

    getCurrentMatchesCount(option): number {
        return this.filter.getCurrentMatchesCount(option);
    }

    isSelected(option): boolean {
        return this.filter.isSelected(option);
    }

    toggle(option): void {
        this.filter.toggle(option);
    }

    get attrsOptionsOrder(): NAttributeOrder.IContent[] | undefined {
        return this.iCmsConfigResource?.getModelByUrl(NAttributeOrder.ID)?.data?.content?.attrs;
    }

    protected sortedOptionsWithMatches() {
        const attrOptionsOrder = this.attrsOptionsOrder
            ?.find(e => e.attr === this.filter.attribute.id)
            ?.order?.map(i => i.optionLabel?.toLocaleLowerCase());
        if (attrOptionsOrder) {
            return this.optionsWithMatches().sort((a, b) => {
                const optionIndex1 = attrOptionsOrder.indexOf(
                    typeof a.label === 'string' ? a.label?.toLocaleLowerCase() : a.label.toString()
                );
                const optionIndex2 = attrOptionsOrder.indexOf(
                    typeof b.label === 'string' ? b.label?.toLocaleLowerCase() : b.label.toString()
                );
                return (optionIndex1 > -1 ? optionIndex1 : Infinity) - (optionIndex2 > -1 ? optionIndex2 : Infinity);
            });
        }
        return this.optionsWithMatches();
    }
}
